import type { ReactNode } from 'react';
import { Button } from '../../Button/Button';
import type { ButtonVariant } from '../../Button/Button.types';
import { SparkIcon } from '../../SparkIcon';
import { FeedbackIcon } from '../../shared/FeedbackIcon';
import type { FeedbackType } from '../../shared/feedbackHelpers';
import '../Modal.css';

/** Header section for modal dialogs with title, subtitle, optional feedback icon, and close button. */
export type ModalHeaderProps = {
  title?: ReactNode;
  subtitle?: string;
  showSubtitle?: boolean;
  showClose?: boolean;
  feedbackType?: FeedbackType;
  /**
   * Renders a 24px back arrow before the title (Figma Modal-Header with
   * back navigation, e.g. a nested "changes" view). The arrow spans the
   * title and subtitle rows.
   */
  onBackClick?: () => void;
  /** Accessible name of the back arrow (default "Back"). */
  backLabel?: string;
  className?: string;
  onCloseClick?: () => void;
};

export function ModalHeader({
  title = 'Long title',
  subtitle = 'Subtitle',
  showSubtitle = true,
  showClose = true,
  feedbackType,
  onBackClick,
  backLabel = 'Back',
  className = '',
  onCloseClick,
}: ModalHeaderProps) {
  return (
    <header
      className={['spark-modal-header', showSubtitle ? 'spark-modal-header--with-subtitle' : '', className]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="spark-modal-header-leading">
        {onBackClick ? (
          <Button
            variant="ghost"
            size="tiny"
            type="button"
            className="spark-modal-header-back"
            aria-label={backLabel}
            icon={<SparkIcon slug="arrow-left" size="medium" />}
            onClick={onBackClick}
          />
        ) : null}
        {feedbackType ? (
          <FeedbackIcon feedbackType={feedbackType} className="spark-modal-header-feedback-icon" />
        ) : null}
        <div className="spark-modal-header-copy">
          <h2 id="spark-modal-title" className="spark-modal-header-title">
            {title}
          </h2>
          {showSubtitle ? (
            <p className="spark-modal-header-subtitle">{subtitle}</p>
          ) : null}
        </div>
      </div>
      {showClose ? (
        <Button
          variant="ghost"
          size="tiny"
          type="button"
          className="spark-modal-header-close"
          aria-label="Close modal"
          icon={<SparkIcon slug="close" size="small" />}
          onClick={onCloseClick}
        />
      ) : null}
    </header>
  );
}

export type ModalFooterType = 'default' | 'checkbox';

/** Footer section for modal dialogs with tertiary + primary buttons and optional checkbox/slot. */
export type ModalFooterProps = {
  /** `'default'` shows buttons only; `'checkbox'` adds a checkbox in the footer slot. */
  footerType?: ModalFooterType;
  danger?: boolean;
  showFooterSlot?: boolean;
  showTertiaryButton?: boolean;
  showMainButton?: boolean;
  checkboxLabel?: string;
  tertiaryLabel?: string;
  /** Optional leading icon for the tertiary button. */
  tertiaryIcon?: ReactNode;
  /** When true, the tertiary footer button is disabled. */
  tertiaryDisabled?: boolean;
  /** Busy state of the tertiary footer button (spinner replaces the label, clicks ignored). */
  tertiaryLoading?: boolean;
  /** Button variant of the leading (tertiary-slot) footer button. */
  tertiaryVariant?: ButtonVariant;
  primaryLabel?: string;
  /** Optional leading icon for the primary button. */
  primaryIcon?: ReactNode;
  /** When true, the primary footer button is disabled. */
  primaryDisabled?: boolean;
  /** Busy state of the primary footer button (spinner replaces the label, clicks ignored). */
  primaryLoading?: boolean;
  /** Disable the primary footer button while a `ReadOnlyProvider` is read-only, with its reason as a tooltip. */
  primaryDisableWhenReadOnly?: boolean;
  footerSlot?: ReactNode;
  className?: string;
  onTertiaryClick?: () => void;
  onPrimaryClick?: () => void;
};

export function ModalFooter({
  footerType = 'default',
  danger = false,
  showFooterSlot = false,
  showTertiaryButton = true,
  showMainButton = true,
  checkboxLabel = 'Checkbox',
  tertiaryLabel = 'Button',
  tertiaryIcon,
  tertiaryDisabled = false,
  tertiaryLoading = false,
  tertiaryVariant = 'tertiary',
  primaryLabel = 'Button',
  primaryIcon,
  primaryDisabled = false,
  primaryLoading = false,
  primaryDisableWhenReadOnly = false,
  footerSlot,
  className = '',
  onTertiaryClick,
  onPrimaryClick,
}: ModalFooterProps) {
  return (
    <footer className={['spark-modal-footer', className].filter(Boolean).join(' ')}>
      {showFooterSlot || footerType === 'checkbox' ? (
        <div className="spark-modal-footer-slot">
          {footerSlot ??
            (footerType === 'checkbox' ? (
              <label className="spark-modal-footer-checkbox">
                <input type="checkbox" />
                <span>{checkboxLabel}</span>
              </label>
            ) : (
              <div className="spark-modal-footer-slot-placeholder" aria-hidden="true" />
            ))}
        </div>
      ) : null}
      <div className="spark-modal-footer-actions">
        {showTertiaryButton ? (
          <Button
            variant={tertiaryVariant}
            size="medium"
            type="button"
            startIcon={tertiaryIcon}
            disabled={tertiaryDisabled}
            loading={tertiaryLoading}
            onClick={onTertiaryClick}
          >
            {tertiaryLabel}
          </Button>
        ) : null}
        {showMainButton ? (
          <Button
            variant="primary"
            size="medium"
            type="button"
            danger={danger}
            disabled={primaryDisabled}
            loading={primaryLoading}
            disableWhenReadOnly={primaryDisableWhenReadOnly}
            startIcon={primaryIcon}
            onClick={onPrimaryClick}
          >
            {primaryLabel}
          </Button>
        ) : null}
      </div>
    </footer>
  );
}
